import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { IncomingReaction } from '../hooks/useReactions';

export interface ReactionButtonProps {
  /** Whether the reaction button is hidden (during ad breaks per Req 1.25/1.26) */
  hidden?: boolean;
  /** WebSocket connected state */
  connected?: boolean;
  /** Send a reaction emoji via WebSocket */
  onSendReaction?: (emoji: string) => void;
  /** Incoming reaction from another viewer */
  incomingReaction?: IncomingReaction | null;
}

interface FloatingEmoji {
  id: number;
  emoji: string;
  x: number;
  delay: number;
}

const EMOTICONS = ['❤️', '🔥', '👏', '💪', '🎶', '🥁', '✨'];

/**
 * Reaction button in the bottom-right corner (Req 1.16).
 * Triggers emoticon reactions broadcast to all viewers via WebSocket.
 * Shows floating emoji animations on tap (Req 1.23).
 * Uses WhatsApp-style emoticons (Req 1.24).
 */
export const ReactionButton: React.FC<ReactionButtonProps> = ({
  hidden = false,
  connected = false,
  onSendReaction,
  incomingReaction,
}) => {
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([]);
  const nextId = useRef(0);

  // Display incoming reactions from other viewers
  useEffect(() => {
    if (!incomingReaction) return;

    const newEmoji: FloatingEmoji = {
      id: nextId.current++,
      emoji: incomingReaction.emoji,
      x: Math.random() * 80 + 10,
      delay: 0,
    };

    setFloatingEmojis((prev) => [...prev, newEmoji]);

    const timer = setTimeout(() => {
      setFloatingEmojis((prev) => prev.filter((e) => e.id !== newEmoji.id));
    }, 5500);

    return () => clearTimeout(timer);
  }, [incomingReaction]);

  const handleReaction = useCallback(() => {
    // Generate 1-7 random emojis (Req 1.23)
    const count = Math.floor(Math.random() * 7) + 1;
    const newEmojis: FloatingEmoji[] = [];

    for (let i = 0; i < count; i++) {
      const emoji = EMOTICONS[Math.floor(Math.random() * EMOTICONS.length)];
      newEmojis.push({
        id: nextId.current++,
        emoji,
        x: Math.random() * 80 + 10,
        delay: Math.random() * 0.5,
      });
    }

    setFloatingEmojis((prev) => [...prev, ...newEmojis]);

    // Clean up after animation (max 5s per Req 1.23)
    setTimeout(() => {
      setFloatingEmojis((prev) =>
        prev.filter((e) => !newEmojis.find((n) => n.id === e.id))
      );
    }, 5500);

    // Broadcast via WebSocket — send the first emoji as the reaction
    if (onSendReaction) {
      const selectedEmoji = newEmojis[0]?.emoji ?? '❤️';
      onSendReaction(selectedEmoji);
    }
  }, [onSendReaction]);

  if (hidden) return null;

  return (
    <>
      {/* Floating emoji area — lower 30% of the video frame (Req 1.23) */}
      <div style={styles.emojiContainer} aria-hidden="true">
        {floatingEmojis.map((item) => (
          <span
            key={item.id}
            style={{
              ...styles.floatingEmoji,
              left: `${item.x}%`,
              animationDelay: `${item.delay}s`,
            }}
          >
            {item.emoji}
          </span>
        ))}
      </div>

      {/* Reaction button — bottom-right corner (Req 1.16) */}
      <div style={styles.buttonContainer}>
        <button
          onClick={handleReaction}
          style={{
            ...styles.button,
            opacity: connected ? 1 : 0.6,
          }}
          aria-label="Envoyer une réaction"
          title="Réaction"
        >
          <HeartIcon />
        </button>
      </div>

      {/* CSS animation keyframes */}
      <style>{floatKeyframes}</style>
    </>
  );
};

const HeartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const floatKeyframes = `
@keyframes floatUp {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  50% {
    opacity: 0.8;
    transform: translateY(-60px) scale(1.2);
  }
  100% {
    opacity: 0;
    transform: translateY(-120px) scale(0.8);
  }
}
`;

const styles: Record<string, React.CSSProperties> = {
  buttonContainer: {
    position: 'absolute',
    bottom: '70px',
    right: '16px',
    zIndex: 10,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    color: '#ff4757',
    border: 'none',
    cursor: 'pointer',
    transition: 'transform 0.2s, background 0.2s',
  },
  emojiContainer: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '30%',
    pointerEvents: 'none',
    overflow: 'hidden',
    zIndex: 9,
  },
  floatingEmoji: {
    position: 'absolute',
    bottom: '0',
    fontSize: '1.5rem',
    animation: 'floatUp 3s ease-out forwards',
    pointerEvents: 'none',
  },
};
